import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { capabilityHooks } from './hooks';
const CAPABILITY_FIELDS = [
    {
        kind: 'text',
        name: 'name',
        label: 'Name',
        required: true,
        maxLength: 50
    },
    {
        kind: 'textarea',
        name: 'description',
        label: 'Description',
        rows: 3
    }
];
// Form defaults for a given record (null = create). Shared by the create modal
// and the drawer's inline edit so both surfaces start from the same shape.
const capabilityDefaults = (c)=>({
        id: c?.id ?? '',
        name: c?.name ?? '',
        description: c?.description ?? ''
    });
// Read sections plus, per row, the inline-edit control. Name/Description reuse
// the SAME field configs the create form defines; Source is derived and stays
// read-only.
function capabilityDetailSections(c) {
    return [
        {
            heading: 'Capability',
            rows: [
                {
                    label: 'Name',
                    value: c.name || '—',
                    editField: {
                        kind: 'text',
                        name: 'name',
                        label: 'Name',
                        required: true,
                        maxLength: 50
                    }
                },
                {
                    label: 'Description',
                    value: c.description || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'description',
                        label: 'Description',
                        rows: 3
                    }
                },
                // Derived, not part of the create form — read-only in edit mode too.
                {
                    label: 'Source',
                    value: c.isSystem ? 'System default' : 'Custom'
                }
            ]
        }
    ];
}
export function CapabilitiesTab() {
    const tenantId = useTenantId();
    const { data, isLoading } = capabilityHooks.useList({
        companyId: tenantId
    });
    const upsert = capabilityHooks.useUpsert();
    const remove = capabilityHooks.useDelete();
    const items = data?.items ?? [];
    const [formOpen, setFormOpen] = useState(false);
    // Store only the selected id and DERIVE the record from `items` — so an open
    // drawer always reflects the latest list without an effect that re-syncs
    // state (which infinite-loops on fresh-but-equal references and hangs).
    const [detailId, setDetailId] = useState(null);
    const detail = detailId ? items.find((c)=>c.id === detailId) ?? null : null;
    const [detailMode, setDetailMode] = useState('read');
    const [deleting, setDeleting] = useState(null);
    const columns = [
        {
            key: 'name',
            header: 'Name'
        },
        {
            key: 'description',
            header: 'Description'
        },
        {
            key: 'source',
            header: 'Source',
            render: (row)=>row.isSystem ? 'System default' : 'Custom'
        }
    ];
    // Shared by the create modal and drawer edit-in-place; the target id is passed
    // in (empty = create).
    const submitCapability = async (values, id)=>{
        await upsert.mutateAsync({
            id,
            companyId: tenantId,
            name: values.name ?? '',
            description: values.description ?? ''
        });
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    // The 6 seeded defaults are read-only (the server refuses writes to _system
    // rows), so the drawer offers edit only on tenant-added rows.
    const canEditDetail = !!detail && !detail.isSystem;
    const detailEdit = detail && !detail.isSystem ? {
        defaultValues: capabilityDefaults(detail),
        onSubmit: (v)=>submitCapability(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes'
    } : undefined;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>setFormOpen(true),
                    "data-testid": Testids.deliveryRegistries.capabilityCreate,
                    children: "New Capability"
                })
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: columns,
                testid: Testids.deliveryRegistries.capabilitiesTable,
                rowTestid: Testids.deliveryRegistries.capabilityRow,
                rowKey: (c)=>c.id,
                isLoading: isLoading,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                rowActions: (row)=>// The 6 seeded defaults are read-only; only tenant-added rows can be
                    // edited or deleted (the server refuses writes to _system rows too).
                    row.isSystem ? /*#__PURE__*/ _jsx("span", {
                        className: "text-xs text-muted-foreground",
                        children: "System default"
                    }) : /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                // Edit-in-place: open the drawer straight into edit mode.
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryRegistries.capabilityEdit,
                                children: "Edit"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryRegistries.capabilityDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Create Capability",
                fields: CAPABILITY_FIELDS,
                defaultValues: capabilityDefaults(null),
                onSubmit: async (v)=>{
                    await submitCapability(v, '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? detail.name || 'Capability' : '',
                sections: detail ? capabilityDetailSections(detail) : [],
                testid: Testids.deliveryRegistries.capabilityDetailDrawer,
                edit: detailEdit,
                canEdit: canEditDetail,
                initialMode: detailMode
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Capability",
                description: deleting ? `Permanently delete "${deleting.name}"? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            })
        ]
    });
}
